import { Form } from 'antd';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Financial } from 'app/components/pages/Hotels/Form/Financial';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useSaveHotelFinance } from 'app/hooks/data/hotels/useHotelFinance';
import { BillingInterval } from 'app/typings/billingIntervals';
import type { FinanceForm } from 'app/typings/hotels/Finance/Finance';

import { HeaderWrapper } from '../HeaderWrapper/HeaderWrapper';

export const FinancialForm: FC<Props> = ({
  handleSubmit,
  initialValues,
  countryId,
  hotelId,
  isLoading = false,
}) => {
  const [form] = Form.useForm();
  const [nextDisabled, setNextDisabled] = useState(false);

  const { data: hotel } = useGetHotel(hotelId);

  const { mutateAsync: saveFinancial } = useSaveHotelFinance(
    hotelId.toString()
  );

  const handleNext = () => {
    form.submit();
  };

  const onFormFinish = async (payload: {
    finance: FinanceForm['finance'];
    siret: string;
  }) => {
    const financePayload = {
      issuingActivated: hotel?.issuingActivated || false,
      finance: {
        bank: payload.finance.bank,
        iban: payload.finance.iban,
        vat: payload.finance.vat,
        invoicing: {
          hotelName: payload.finance.invoicing.hotelName,
          gender: payload.finance.invoicing.gender,
          firstName: payload.finance.invoicing.firstName,
          lastName: payload.finance.invoicing.lastName,
          email: payload.finance.invoicing.email,
          city: payload.finance.invoicing.city,
          zipCode: payload.finance.invoicing.zipCode,
          street: payload.finance.invoicing.street,
          country: payload.finance.invoicing.country,
        },
      },
      siret: payload.siret,
      billingInterval: hotel?.billingInterval || BillingInterval.MONTHLY,
    };

    await saveFinancial(financePayload);

    handleSubmit();
  };

  const validateForm = useCallback(async () => {
    try {
      await form.validateFields();
      setNextDisabled(false);
    } catch {
      setNextDisabled(true);
    }
  }, [form]);

  useEffect(() => {
    validateForm();
  }, [form, validateForm]);

  return (
    <HeaderWrapper
      nextDisabled={nextDisabled}
      handleNext={handleNext}
      isLoading={isLoading}
    >
      <Form
        form={form}
        onFinish={onFormFinish}
        initialValues={initialValues}
        onChange={validateForm}
      >
        <Financial
          mode="edit"
          displayStripe={false}
          isIbanRequired
          isBuilder
          isVATRequired
          countryId={countryId}
        />
      </Form>
    </HeaderWrapper>
  );
};

type Props = {
  handleSubmit: () => void;
  initialValues: {};
  countryId?: number;
  hotelId: number;
  isLoading?: boolean;
};
